import { Link } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Chip } from '@nyce/admin-kit/ui';
import { SCOPES, type Environment } from '../../api';
import { logsLink } from '../../lib/logFilters';
import { useHubLogin } from '../../session';
import { identityPath } from '../../identity/session';
import type { StatsLinks } from './links';

/**
 * Correlation (review §3): one click from statistics to the hub's pods, logs and database of the same environment.
 * `since` makes the logs link open the same time window.
 */
export function EnvLinks({
  envs,
  via,
  links,
  since,
}: {
  envs: Environment[];
  via?: 'workserver' | 'organization' | null;
  links: StatsLinks & { loading: boolean };
  since?: Date;
}) {
  const hub = useHubLogin();
  const scopes = hub?.scopes;
  if (links.loading) return <span className="nyk-ui-hint">Looking up environments…</span>;
  if (!envs.length)
    return (
      <span className="nyk-ui-hint" data-testid="env-links-none">
        {links.reason ??
          'No linked environment: give an environment its AUTH tenant (Admin › Environments) and the tenant a workserver alias, or the AUTH customer a pms alias.'}
      </span>
    );
  return (
    <div className="sx-links" data-testid="env-links">
      {via === 'organization' && <span className="nyk-ui-hint">Environments of the organization:</span>}
      {envs.map((e) => (
        <span key={e.id} className="sx-links" data-env={e.id}>
          <Chip size="sm">{e.name}</Chip>
          {e.clusterId && hasScope(scopes, SCOPES.podsRead) && (
            <Link className="nyk-link" to={`/pods?env=${encodeURIComponent(e.id)}`}>
              Pods
            </Link>
          )}
          {e.clusterId && hasScope(scopes, SCOPES.gcplogsRead) && (
            <Link className="nyk-link" to={logsLink(e.id, undefined, undefined, { since })}>
              Logs
            </Link>
          )}
          {e.dbServerId && hasScope(scopes, SCOPES.dbRead) && (
            <Link
              className="nyk-link"
              to={`/databases?server=${encodeURIComponent(e.dbServerId)}&db=${encodeURIComponent(e.dbName)}`}
            >
              Database
            </Link>
          )}
          {e.authTenantId && links.target && hasScope(scopes, SCOPES.authRead) && (
            <Link className="nyk-link" to={identityPath(`/identity/tenants/${e.authTenantId}`, links.target)}>
              Tenant
            </Link>
          )}
        </span>
      ))}
    </div>
  );
}
